// src/pages/CoreAssure/WeldAssure/WeldAssure.jsx
//
// CoreAssure WeldAssure (D-048 §4.2) — Day 3-5 MVP page.
//
// Pre-flight validation check across 5 of v2 §11.2 categories
// (WPS approval, welder qual, thickness, material, expiration).
// Standalone — does NOT inject into existing weld-task assign middleware.
// Phase 2 wires the same validation into middleware position with
// §5 Reviewer review.
//
// Per-tenant feature flag default OFF for tenant 2 (Chesakl) until Day 14
// of pilot (later of Day 14 or pilot ≥ 7 days clean = 2026-06-27).

import React, { useEffect, useState, useCallback } from "react";
import {
  Container, Stack, Typography, Card, CardContent, Grid,
  Select, MenuItem, FormControl, InputLabel, Button,
  Breadcrumbs, Box, CircularProgress, Alert, Tooltip,
} from "@mui/material";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { toast } from "react-toastify";

import ApiCall from "../../../utils/apicall";
import { weldAssureApi } from "../../../utils/coreassure/weldAssureApi";
import WeldAssureResultCard from "../../../components/CoreAssure/WeldAssure/WeldAssureResultCard";
import WeldAssureCategoryGrid from "../../../components/CoreAssure/WeldAssure/WeldAssureCategoryGrid";

export default function WeldAssure() {
  const navigate = useNavigate();
  const [welders, setWelders] = useState([]);
  const [wpsList, setWpsList] = useState([]);
  const [weldTaskInput, setWeldTaskInput] = useState("");
  const [selectedWelderId, setSelectedWelderId] = useState("");
  const [selectedWpsId, setSelectedWpsId] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingMeta, setLoadingMeta] = useState(true);
  const [previewDisabled, setPreviewDisabled] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  // Load welders + WPS lists in parallel (tenant-scoped server-side per
  // existing convention).
  useEffect(() => {
    let cancelled = false;
    setLoadingMeta(true);
    Promise.allSettled([
      ApiCall.get("/welders"),
      ApiCall.get("/wps"),
    ]).then(([weldersRes, wpsRes]) => {
      if (cancelled) return;
      if (weldersRes.status === "fulfilled") {
        const list = weldersRes.value?.data?.data || weldersRes.value?.data || [];
        setWelders(Array.isArray(list) ? list : []);
      }
      if (wpsRes.status === "fulfilled") {
        const list = wpsRes.value?.data?.data || wpsRes.value?.data || [];
        setWpsList(Array.isArray(list) ? list : []);
      }
    }).finally(() => { if (!cancelled) setLoadingMeta(false); });
    return () => { cancelled = true; };
  }, []);

  const runCheck = useCallback(async () => {
    const weldTaskId = parseInt(weldTaskInput, 10);
    if (!Number.isInteger(weldTaskId) || weldTaskId <= 0) {
      setError("Enter a valid weld-task ID (positive integer).");
      return;
    }
    if (!selectedWelderId) {
      setError("Pick a welder.");
      return;
    }
    setLoading(true);
    setError(null);
    setPreviewDisabled(false);
    try {
      const res = await weldAssureApi.check({
        weldTaskId,
        welderId: selectedWelderId,
        wpsId: selectedWpsId || null,
      });
      const envelope = res?.data?.data || null;
      setResult(envelope);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 403) {
        setPreviewDisabled(true);
        setResult(null);
      } else if (status === 404) {
        setError("Weld-task / welder / WPS not found in your tenant.");
      } else if (status === 400) {
        setError(err?.response?.data?.error || "Invalid input — check the fields above.");
      } else {
        const msg = err?.response?.data?.error || err.message || "Failed to run WeldAssure check";
        setError(msg);
        toast.error(msg);
      }
    } finally {
      setLoading(false);
    }
  }, [weldTaskInput, selectedWelderId, selectedWpsId]);

  return (
    <Container maxWidth="xl">
      <Helmet><title>WeldAssure — Pre-flight check | SFM</title></Helmet>

      <Breadcrumbs sx={{ mb: 1 }}>
        <RouterLink to="/dashboard/app" style={{ color: "inherit" }}>Home</RouterLink>
        <Typography color="text.primary">CoreAssure</Typography>
        <Typography color="text.primary">WeldAssure</Typography>
      </Breadcrumbs>

      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h4">WeldAssure — Pre-flight check</Typography>
          <Typography variant="body2" color="text.secondary">
            Validate WPS approval + welder qualification + thickness/material/expiration before assigning a weld task.
            CoreAssure powered. Read-only preview; advisory result.
          </Typography>
        </Box>
      </Stack>

      {previewDisabled && (
        <Alert severity="info" sx={{ mb: 2 }}>
          WeldAssure preview is not enabled for this tenant. Per D-048 §6 pilot guardrails,
          the per-tenant feature flag <code>coreassure_weld_enabled</code> is OFF by default.
          Contact your administrator to enable.
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      )}

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="flex-end">
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel id="weld-task-label" shrink>Weld-task ID</InputLabel>
                <input
                  type="number"
                  min="1"
                  value={weldTaskInput}
                  onChange={(e) => { setWeldTaskInput(e.target.value); setResult(null); setError(null); setPreviewDisabled(false); }}
                  placeholder="e.g. 42"
                  style={{
                    padding: "16px 14px",
                    border: "1px solid rgba(0,0,0,0.23)",
                    borderRadius: "4px",
                    fontSize: "16px",
                    width: "100%",
                  }}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel id="welder-label">Welder</InputLabel>
                <Select
                  labelId="welder-label"
                  value={selectedWelderId}
                  label="Welder"
                  onChange={(e) => { setSelectedWelderId(e.target.value); setResult(null); }}
                  disabled={loadingMeta}
                >
                  {loadingMeta && <MenuItem disabled value="">Loading…</MenuItem>}
                  {!loadingMeta && welders.length === 0 && <MenuItem disabled value="">No welders available</MenuItem>}
                  {welders.map((w) => (
                    <MenuItem key={w.id} value={w.id}>
                      {w.name} {w.stamp ? `(${w.stamp})` : ""}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel id="wps-label">WPS (optional)</InputLabel>
                <Select
                  labelId="wps-label"
                  value={selectedWpsId}
                  label="WPS (optional)"
                  onChange={(e) => { setSelectedWpsId(e.target.value); setResult(null); }}
                  disabled={loadingMeta}
                >
                  <MenuItem value="">— Use weld-task's assigned WPS —</MenuItem>
                  {wpsList.map((w) => (
                    <MenuItem key={w.id} value={w.id}>
                      {w.code || w.name || `WPS #${w.id}`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <Tooltip title="Run the WeldAssure validation. Each run writes one CA-WELD-CHECK row in audit_log (immutable).">
                <span>
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={loading ? <CircularProgress size={16} /> : <PlayCircleOutlineIcon />}
                    onClick={runCheck}
                    disabled={loading || !weldTaskInput || !selectedWelderId}
                  >
                    {loading ? "Validating…" : "Run check"}
                  </Button>
                </span>
              </Tooltip>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {result && (
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <WeldAssureResultCard result={result} />
          </Grid>
          <Grid item xs={12} md={8}>
            <WeldAssureCategoryGrid result={result} navigate={navigate} />
          </Grid>
        </Grid>
      )}

      {!result && !loading && !previewDisabled && !error && (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="body2" color="text.secondary">
              Enter a <b>weld-task ID</b>, pick a <b>welder</b>, optionally pick a <b>WPS</b>, then click <b>Run check</b>.
              The validation is read-only; it does not modify weld-task state.
            </Typography>
          </CardContent>
        </Card>
      )}
    </Container>
  );
}
